import { type RefObject, useEffect, useRef, useState } from 'react'

interface UseResizeObserverResult<E extends HTMLElement> {
  ref: RefObject<E | null>
  width: number | undefined
}

/**
 * Primitive hook that observes an element's width via ResizeObserver.
 *
 * Returns `undefined` for width until the first measurement fires,
 * making the "not yet measured" state explicit for consumers.
 *
 * @returns Object with `ref` to attach to the element and `width` (undefined until measured)
 *
 * @example
 * ```tsx
 * const { ref, width } = useResizeObserver<HTMLDivElement>()
 * return <div ref={ref}>Width: {width ?? 'measuring...'}</div>
 * ```
 */
export function useResizeObserver<E extends HTMLElement = HTMLElement>(): UseResizeObserverResult<E> {
  const ref = useRef<E | null>(null)
  const [width, setWidth] = useState<number | undefined>(undefined)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(([entry]) => {
      const newWidth = entry.contentBoxSize[0].inlineSize
      setWidth((prev) => (prev === newWidth ? prev : newWidth))
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [])

  return { ref, width }
}
